
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typography, TypographyProps } from "@/components/ui/typography/base";
import { cn } from "@/lib/utils";

interface HoverGlowTextProps extends Omit<TypographyProps, "children"> {
  children: string;
  className?: string;
  glowColor?: string;
  dimColor?: string;
  as?: React.ElementType;
}

export const HoverGlowText: React.FC<HoverGlowTextProps> = ({
  children,
  className,
  variant = "muted",
  size = "body",
  weight,
  as,
  glowColor,
  dimColor,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Split text into words first, then characters
  const words = children.split(" ");

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isFocused) return;
    
    // Calculate total characters for navigation
    const totalChars = children.replace(/\s+/g, "").length;
    
    if (e.key === 'ArrowRight' && hoveredIndex !== null && hoveredIndex < totalChars - 1) {
      setHoveredIndex(hoveredIndex + 1);
    } else if (e.key === 'ArrowLeft' && hoveredIndex !== null && hoveredIndex > 0) {
      setHoveredIndex(hoveredIndex - 1);
    } else if (e.key === 'Home' && isFocused) {
      setHoveredIndex(0);
    } else if (e.key === 'End' && isFocused) {
      setHoveredIndex(totalChars - 1);
    }
  }, [hoveredIndex, isFocused, children]);

  // Add keyboard event listeners
  useEffect(() => {
    if (isFocused) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isFocused, handleKeyDown]);

  // Determine which class to apply based on distance from hovered index
  // Reduced range from 0-5 to 0-3 (about 1.5x decrease)
  const getCharacterClass = (globalIndex: number) => {
    if (hoveredIndex === null) return '';
    
    const distance = Math.abs(globalIndex - hoveredIndex);
    
    if (distance === 0) {
      return 'glow-character';
    } else if (distance === 1) {
      return 'glow-neighbor-1';
    } else if (distance === 2) {
      return 'glow-neighbor-2';
    } else if (distance === 3) {
      return 'glow-distant-1';
    } else {
      return 'dim-character';
    }
  };

  return (
    <Typography
      as={as || "div"}
      variant={variant}
      size={size}
      weight={weight}
      className={cn(className)}
      {...props}
    >
      <span 
        ref={containerRef}
        className="hover-glow-text"
        tabIndex={0}
        role="text"
        aria-label={children}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setHoveredIndex(null);
        }}
        onMouseLeave={() => {
          if (!isFocused) setHoveredIndex(null);
        }}
      >
        {words.map((word, wordIndex) => {
          // Calculate global index offset for this word
          let globalCharOffset = 0;
          for (let i = 0; i < wordIndex; i++) {
            globalCharOffset += words[i].length;
          }
          
          return (
            <span key={`word-${wordIndex}`} className="inline-block">
              {/* Render each character in the word */}
              {word.split("").map((char, charIndex) => {
                // Calculate the global index for this character
                const globalCharIndex = globalCharOffset + charIndex;
                
                return (
                  <span
                    key={`char-${wordIndex}-${charIndex}`}
                    className={cn(
                      "hover-glow-character",
                      getCharacterClass(globalCharIndex)
                    )}
                    onMouseEnter={() => setHoveredIndex(globalCharIndex)}
                    aria-hidden="true"
                    style={{
                      // Apply custom colors if provided
                      ...(glowColor && getCharacterClass(globalCharIndex) === 'glow-character' ? 
                        { color: glowColor, textShadow: `0 0 8px ${glowColor}` } : {}),
                      ...(dimColor && getCharacterClass(globalCharIndex) === 'dim-character' ? 
                        { color: dimColor } : {})
                    }}
                  >
                    {char}
                  </span>
                );
              })}
              {/* Add a space after each word except the last one */}
              {wordIndex < words.length - 1 && <span className="inline-block">&nbsp;</span>}
            </span>
          );
        })}
      </span>
    </Typography>
  );
};

export default HoverGlowText;
